"use client";

import { MapPin } from "lucide-react";
import React, { useEffect, useRef } from "react";

interface StaticMapPinLayerProps {
  /** Wrapper around the embedded MapView; the marker is positioned relative to it. */
  mapWrapperRef: React.RefObject<HTMLDivElement | null>;
  /** The pin position in SVG user space (survives pan & zoom). */
  point: { x: number; y: number };
}

/** The map's floor `<svg>` is the first svg inside the `.map-container` element. */
const getMapSvg = (wrapper: HTMLDivElement | null): SVGSVGElement | null =>
  (wrapper?.querySelector(".map-container svg") as SVGSVGElement | null) ??
  null;

/**
 * Read-only pin rendered on an embedded map at a fixed SVG coordinate. Mirrors
 * MapPointPickerLayer's positioning (createSVGPoint + getScreenCTM, glued on
 * pan/zoom) but without any picking interaction — used to show where a workplace
 * request was pinned.
 */
export const StaticMapPinLayer: React.FC<StaticMapPinLayerProps> = ({
  mapWrapperRef,
  point
}) => {
  const markerRef = useRef<HTMLDivElement | null>(null);

  // Keep the marker glued to its map coordinate (relative to the wrapper) on
  // pan/zoom. Re-query the `<svg>` every frame: on an embedded map it loads
  // asynchronously, so it may not exist when this effect first runs. The marker
  // stays hidden until it can be positioned, to avoid a flash at (0,0).
  useEffect(() => {
    const wrapper = mapWrapperRef.current;
    if (!wrapper) return;
    let raf = 0;
    const tick = () => {
      const svg = getMapSvg(wrapper);
      const el = markerRef.current;
      const ctm = svg?.getScreenCTM();
      if (svg && ctm && el) {
        const p = svg.createSVGPoint();
        p.x = point.x;
        p.y = point.y;
        const screen = p.matrixTransform(ctm);
        const rect = wrapper.getBoundingClientRect();
        el.style.transform = `translate(${screen.x - rect.left}px, ${
          screen.y - rect.top
        }px)`;
        el.style.visibility = "visible";
      } else if (el) {
        el.style.visibility = "hidden";
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [point.x, point.y, mapWrapperRef]);

  return (
    <div
      ref={markerRef}
      data-map-point
      className="pointer-events-none invisible absolute top-0 left-0 z-[50]"
    >
      {/* Pin marker — tip sits exactly on the pinned map point. */}
      <div className="-translate-x-1/2 -translate-y-full absolute">
        <span className="relative flex items-center justify-center">
          <span className="absolute h-5 w-5 rounded-full bg-purple-500/30 blur-[3px]" />
          <MapPin className="relative h-7 w-7 fill-purple-600 text-white drop-shadow" />
        </span>
      </div>
    </div>
  );
};
